import { Autocomplete, AutocompleteProps, createFilterOptions, FilterOptionsState } from '@material-ui/lab';
import React, { useCallback, useLayoutEffect, useState } from 'react';

export type AutocompleteInputProps = AutocompleteProps<string, false, true, true | false> & {
  deferHandleOnChange?: boolean;
  preventTabOnChange?: boolean;
};

export const AutocompleteInput = (props: AutocompleteInputProps) => {
  const { preventTabOnChange, deferHandleOnChange = true, ...restOfProps } = props;
  const allowedToClear = !props.disableClearable;
  const [temporaryValue, setTemporaryValue] = useState(props.value ?? '');
  const handleFilterOptions = useCallback(
    (options: string[], params: FilterOptionsState<string>) => {
      const filter = createFilterOptions<string>();
      const filtered = filter(options, params);
      const withEmptyOption = temporaryValue === '' ? ['', ...filtered] : filtered;

      if (props.freeSolo && temporaryValue && !options.includes(temporaryValue)) {
        return withEmptyOption.concat(temporaryValue);
      }

      return withEmptyOption;
    },
    [temporaryValue, props.freeSolo],
  );

  // To achieve the ability to clear out a field without the `autoSelect` and `autoHighlight` effects conflicting,
  // we prepend an empty string to the list of options. This makes it so that when the input is empty (i.e. after being cleared)
  // the "highlighted option" is the empty string. For UX purposes, we do not want to explicitly show this empty option to the user.
  // The following workaround is used because the Listbox generated by the autocomplete is not a child of this component, but is instead
  // rendered via portal at the top level of the document.
  //
  // NOTE: This can be fixed when upgraded to MUI v5.
  const hideEmptyOption = useCallback(() => {
    setTimeout(() => {
      const listboxes = Array.from(document.querySelectorAll('[data-listbox=true]'));

      for (const listbox of listboxes) {
        const [firstOption] = Array.from(listbox.children);
        const displayStyle = firstOption.childNodes?.length === 0 ? 'none' : 'flex';

        firstOption.setAttribute('style', `display: ${displayStyle}`);
      }
    }, 0);
  }, []);

  useLayoutEffect(() => {
    for (const element of Array.from(
      document.querySelectorAll('.MuiAutocomplete-clearIndicator:not(.multiselect-autocomplete)'),
    )) {
      (element as HTMLElement).style.display = 'none';
    }
  }, []);

  return (
    <Autocomplete
      autoComplete={true}
      autoSelect={true}
      autoHighlight={true}
      closeIcon={null}
      filterOptions={handleFilterOptions}
      selectOnFocus={true}
      inputValue={props.freeSolo ? temporaryValue : undefined}
      {...restOfProps}
      onOpen={(event) => {
        props.onOpen?.(event);
        hideEmptyOption();
      }}
      onBlur={(event) => {
        if (props.freeSolo && !temporaryValue && props.value && !allowedToClear) {
          setTemporaryValue(props.value);
        }

        props.onBlur?.(event);
      }}
      onChange={(event, value, reason) => {
        if (preventTabOnChange && reason === 'blur') {
          return;
        }
        if (value || allowedToClear) {
          const handleOnChange = () => props.onChange?.(event, value ?? '', reason);
          deferHandleOnChange ? setTimeout(handleOnChange, 0) : handleOnChange();
        }
      }}
      onInputChange={(event, value, reason) => {
        setTemporaryValue(value);
        props.onInputChange?.(event, value, reason);
        hideEmptyOption();
      }}
      ListboxProps={{ ...props.ListboxProps, 'data-listbox': true }}
      getOptionSelected={(option, value) => option === value}
    />
  );
};
